import { Keyboard, X } from "lucide-react"

import { cn } from "@aix/ui"

import type { SeoTabValue } from "./seoShortcutModel"
import { SEO_CONTEXT_SHORTCUTS, SEO_GLOBAL_SHORTCUTS, SEO_TAB_LABELS } from "./seoShortcutModel"

export { SEO_TAB_VALUES, type SeoTabValue } from "./seoShortcutModel"

export function SEOShortcutLegend({
  activeTab,
  open,
  onOpenChange,
}: {
  activeTab: SeoTabValue
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-expanded="false"
        aria-controls="seo-shortcut-guide"
        aria-keyshortcuts="?"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-xs shadow-lg transition-colors hover:bg-muted"
      >
        <Keyboard className="h-4 w-4" /> Press ? for shortcuts
      </button>
    )
  }

  const contextual = SEO_CONTEXT_SHORTCUTS[activeTab]
  return (
    <aside
      id="seo-shortcut-guide"
      aria-label="SEO keyboard shortcuts"
      className="fixed bottom-4 right-4 z-40 w-72 rounded-lg border border-border bg-card p-3 shadow-xl"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">SEO shortcuts</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{SEO_TAB_LABELS[activeTab]} context</p>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close shortcut guide"
          className="rounded p-1 transition-colors hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-1.5">
        {SEO_GLOBAL_SHORTCUTS.map(([keys, label]) => (
          <ShortcutRow key={keys} keys={keys} label={label} />
        ))}
        {contextual.length ? <div className="my-2 border-t border-border" /> : null}
        {contextual.map(([keys, label]) => (
          <ShortcutRow key={keys} keys={keys} label={label} />
        ))}
      </div>

      <p className={cn("mt-3 border-t border-border pt-2 text-[11px] leading-relaxed text-muted-foreground")}>
        Approval, publishing, pruning, paid generation, and rollback always require visible controls and confirmation.
      </p>
    </aside>
  )
}

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono">{keys}</kbd>
    </div>
  )
}
